import { supabase } from '../lib/supabase';

/**
 * Get or create a referral code for the current user's creator profile.
 * If no creator profile exists, creates one with a generated code.
 */
export async function getOrCreateReferralCode(userId) {
  try {
    // Check if creator profile exists
    const { data: creator, error: fetchErr } = await supabase?.from('creators')?.select('id, referral_code, username, display_name')?.eq('user_id', userId)?.maybeSingle();

    if (fetchErr) throw fetchErr;

    if (creator?.referral_code) {
      return { code: creator?.referral_code, creatorId: creator?.id };
    }

    // Generate a code via RPC
    const { data: code, error: codeErr } = await supabase?.rpc('generate_referral_code', { p_user_id: userId });

    if (codeErr) throw codeErr;

    if (creator) {
      // Update existing creator with code
      const { error: updateErr } = await supabase?.from('creators')?.update({ referral_code: code })?.eq('user_id', userId);
      if (updateErr) throw updateErr;
      return { code, creatorId: creator?.id };
    }

    // Create minimal creator profile
    const { data: profile } = await supabase?.from('user_profiles')?.select('email, full_name')?.eq('id', userId)?.maybeSingle();

    const username = (profile?.email || '')?.split('@')?.[0]?.replace(/[^a-z0-9]/gi, '')?.toLowerCase() || `user${Date.now()}`;
    const displayName = profile?.full_name || username;

    const { data: newCreator, error: createErr } = await supabase?.from('creators')?.insert({ user_id: userId, username, display_name: displayName, referral_code: code })?.select('id')?.single();

    if (createErr) throw createErr;
    return { code, creatorId: newCreator?.id };
  } catch (err) {
    console.error('[ReferralService] getOrCreateReferralCode:', err?.message);
    return { code: null, creatorId: null, error: err?.message };
  }
}

/**
 * Get referral stats for a user: total referrals and total credits earned.
 */
export async function getReferralStats(userId) {
  try {
    const { data, error } = await supabase?.from('creator_referrals')?.select('id, created_at')?.eq('referrer_user_id', userId);

    if (error) throw error;

    const totalReferrals = data?.length || 0;
    // Each referral grants 100 video seconds (credits)
    const totalCreditsEarned = totalReferrals * 100;

    return { totalReferrals, totalCreditsEarned, referrals: data || [] };
  } catch (err) {
    console.error('[ReferralService] getReferralStats:', err?.message);
    return { totalReferrals: 0, totalCreditsEarned: 0, referrals: [] };
  }
}

/**
 * Apply a referral code for a newly signed-up user.
 */
export async function applyReferralCode(referralCode, newUserId) {
  try {
    const { data, error } = await supabase?.rpc('apply_referral_signup', {
        p_referral_code: referralCode,
        p_new_user_id: newUserId,
      });

    if (error) throw error;
    return data;
  } catch (err) {
    console.error('[ReferralService] applyReferralCode:', err?.message);
    return { success: false, error: err?.message };
  }
}

/**
 * Upgrade compute wallet quotas when a user upgrades their plan.
 */
export async function upgradeComputeWalletForPlan(userId, planName) {
  try {
    const { error } = await supabase?.rpc('upgrade_compute_wallet_for_plan', {
        p_user_id: userId,
        p_plan: planName,
      });
    if (error) throw error;
    return { success: true };
  } catch (err) {
    console.error('[ReferralService] upgradeComputeWalletForPlan:', err?.message);
    return { success: false, error: err?.message };
  }
}
